"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { CheckCircle2, Clock, Heart } from "lucide-react";

const TRIGGERS = [
  {
    icon: CheckCircle2,
    title: { en: "Order ready", es: "Orden lista" },
    sub: { en: "KDS bump → instant SMS. \"Pickup at the counter.\"", es: "KDS bump → SMS instantáneo. \"Pickup en el counter.\"" },
    tag: { en: "Automatic", es: "Automático" },
  },
  {
    icon: Clock,
    title: { en: "Order confirmed", es: "Orden confirmada" },
    sub: { en: "Order placed → a confirmation with their order number. No \"did it go through?\" calls.", es: "Orden hecha → una confirmación con su número de orden. Sin llamadas de \"¿sí entró?\"." },
    tag: { en: "Automatic", es: "Automático" },
  },
  {
    icon: Heart,
    title: { en: "Loyalty points", es: "Puntos de lealtad" },
    sub: { en: "Order paid → \"You earned 10 points.\" The text becomes a reason to come back.", es: "Orden pagada → \"Ganaste 10 puntos.\" El SMS es razón para volver." },
    tag: { en: "Automatic", es: "Automático" },
  },
];

export default function SmsCustomerScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div
        className="absolute top-1/4 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.08) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Customer SMS triggers", "Triggers SMS al cliente")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Stop shouting. ", "Deja de gritar. ")}
            <span className="text-redy-charcoal/30">{t("Text them.", "Mándales SMS.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "\"Maria, party of 4!\" across a packed dining room. Or: an SMS that buzzes their pocket — for customers who opted in. Less awkward for them, less wear on your voice.",
              "\"¡Maria, party of 4!\" gritado en un salón lleno. O: un SMS que vibra en su bolsillo — para clientes que aceptaron recibirlos. Menos awkward para ellos, menos desgaste para tu voz."
            )}
          </p>
        </motion.div>

        {/* 3 trigger cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {TRIGGERS.map((trig, i) => {
            const Icon = trig.icon;
            return (
              <motion.div
                key={trig.title.en}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-[#FAFAF8] border border-black/5 rounded-[20px] p-6 relative"
              >
                {/* Trigger chip */}
                <div className="absolute top-5 right-5 text-[10px] font-black tabular-nums" style={{ color: "#F4722B" }}>
                  {t(trig.tag.en, trig.tag.es)}
                </div>

                <div
                  className="w-11 h-11 rounded-[12px] flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(244,114,43,0.12)" }}
                >
                  <Icon className="w-5 h-5" style={{ color: "#F4722B" }} />
                </div>
                <div className="text-[22px] font-black text-redy-charcoal mb-3 tracking-tight">
                  {t(trig.title.en, trig.title.es)}
                </div>
                <div className="text-[13px] font-medium text-redy-secondary leading-relaxed">
                  {t(trig.sub.en, trig.sub.es)}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
